/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Fragment from 'ember-data-model-fragments/fragment';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragmentArray } from 'ember-data-model-fragments/attributes';
import type SessionService from '@linc-technologies/linced-common/services/session';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import type Rating from 'linced-app/models/coach/rating';
import type Person from 'linced-app/models/person';

// Proto: coach.GoalActivity.
export default class GoalActivity extends Fragment {
  declare [Type]: 'coach/goal-activity';

  @service
  declare session: SessionService;

  // title contains the activity's name.
  @attr('string', { defaultValue: '' })
  declare title: string;

  // description contains a brief description of the learning activity
  @attr('string', { defaultValue: '' })
  declare description: string;

  // instructions lists the necessary instructions, directed at the teacher.
  @array('string')
  declare instructions: StatefulArray<string>;

  // ratings stores user ratings to check if a user has rated a given suggestion
  // and what rating they have given it.
  @fragmentArray('coach/rating')
  declare ratings: FragmentArray<Rating>;

  // ratingCount specifies how many ratings have been awarded for this activity.
  @attr('number', { defaultValue: 0 })
  declare ratingCount: number;

  // ratingSum provides the summation of all ratings to determine the average.
  @attr('number', { defaultValue: 0 })
  declare ratingSum: number;

  // ratingAverage calculates the mean rating score. It is 0 with no ratings.
  @computed('ratingSum', 'ratingCount')
  get ratingAverage() {
    if (this.ratingCount === 0) {
      return 0;
    }
    const avg = this.ratingSum / this.ratingCount;
    return Math.round(avg * 100) / 100;
  }

  // userRating retrieves the current user's rating of this goal plan
  @computed('ratings.[]')
  get userRating() {
    const rating = findBy(
      this.ratings,
      'personId',
      // TS-STANDIN(session): common session.js is JS, so person infers as null.
      (this.session.person as unknown as Person).id,
    );
    if (rating !== undefined) {
      return rating.rating;
    }
    return 0;
  }
}
